import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Copy, ImageOff, Check, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BannerCard = ({ banner, onUploaded }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);

  const publicUrl = `${window.location.origin}/api/banners/${banner.filename}`;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(png|jpe?g)$/i.test(file.name)) {
      toast.error("Envie um arquivo PNG ou JPG.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/banners/${banner.slot}/upload`, form);
      if (data.width !== banner.expected.width || data.height !== banner.expected.height) {
        toast.warning(`Enviado, mas atenção: o tamanho é ${data.width}×${data.height} e o esperado é ${banner.expected.width}×${banner.expected.height}.`);
      } else {
        toast.success(`Banner ${data.width}×${data.height} publicado!`);
      }
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no upload. Tente novamente.");
    } finally {
      setUploading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("URL copiada!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  return (
    <div data-testid={`banner-card-${banner.slot}`} className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#121212] p-6 transition-colors duration-300 hover:border-[rgba(212,175,55,0.35)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">{banner.expected.width} × {banner.expected.height} px</p>
          <h3 className="font-serif-display mt-2 text-lg font-semibold text-white">{banner.filename}</h3>
        </div>
        <span className={`font-mono-label rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${banner.uploaded ? "bg-[rgba(212,175,55,0.12)] text-[#D4AF37]" : "bg-[rgba(255,255,255,0.05)] text-[#71717A]"}`}>
          {banner.uploaded ? "No ar" : "Vazio"}
        </span>
      </div>

      <div className="mt-5 flex min-h-[120px] items-center justify-center overflow-hidden rounded-xl border border-dashed border-[rgba(255,255,255,0.12)] bg-[#0d0d0d] p-4">
        {banner.uploaded ? (
          <img
            data-testid={`banner-preview-${banner.slot}`}
            src={`${publicUrl}?v=${encodeURIComponent(banner.updated_at || "")}`}
            alt={banner.slot}
            className="max-h-56 max-w-full object-contain"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-[#3f3f46]">
            <ImageOff className="h-8 w-8" strokeWidth={1.5} />
            <span className="text-xs">Nenhuma imagem enviada</span>
          </div>
        )}
      </div>

      {banner.uploaded && (
        <p className="mt-3 text-xs text-[#71717A]">
          {banner.width}×{banner.height}px · {(banner.size / 1024).toFixed(0)} KB
          {banner.updated_at && <> · atualizado {new Date(banner.updated_at).toLocaleString("pt-BR")}</>}
        </p>
      )}

      <input ref={inputRef} data-testid={`banner-upload-input-${banner.slot}`} type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={handleFile} />
      <button
        data-testid={`banner-upload-button-${banner.slot}`}
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="group mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#F3E5AB] disabled:opacity-50"
      >
        {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {uploading ? "Enviando..." : banner.uploaded ? "Substituir imagem" : "Enviar imagem"}
      </button>

      <div className="mt-4">
        <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[#71717A]">URL pública direta</p>
        <div className="mt-2 flex items-center gap-2">
          <code data-testid={`banner-url-${banner.slot}`} className="block min-w-0 flex-1 truncate rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#0d0d0d] px-3 py-2 text-[11px] text-[#A1A1AA]">
            {publicUrl}
          </code>
          <button
            data-testid={`banner-copy-button-${banner.slot}`}
            onClick={copy}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors duration-300 ${copied ? "border-[#D4AF37] bg-[#D4AF37] text-[#0A0A0A]" : "border-[rgba(212,175,55,0.4)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)]"}`}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copiado" : "Copiar URL"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/banners`);
      setBanners(data);
    } catch {
      toast.error("Não foi possível carregar os banners.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-banners-page" className="min-h-screen bg-[#0A0A0A] px-6 py-14 lg:px-12">
      <div className="mx-auto max-w-6xl">
        <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Área administrativa</p>
        <h1 className="font-serif-display mt-4 text-3xl font-semibold text-white sm:text-4xl">
          Banners de afiliados
          <span className="italic text-[#D4AF37]"> — hospedagem pública</span>
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#A1A1AA] sm:text-base">
          Envie ou substitua cada banner e copie a URL direta do arquivo para cadastrar na Monetizze.
          A URL não muda quando você substitui a imagem.
        </p>

        {loading ? (
          <p className="mt-20 text-center text-sm text-[#71717A]">Carregando...</p>
        ) : (
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {banners.map((b) => (
              <BannerCard key={b.slot} banner={b} onUploaded={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBanners;
